import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Center,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  BidStatus,
  FrameworkAgreementType,
  Currency,
  NegotiationRoundStatus,
  SourcingEventDto,
  type EvalBidDto,
} from "../../../autogen/bff-api";
import { CompleteRoundButton } from "../negotiation/CompleteRoundButton";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { AwardBidsTable } from "./AwardBidsTable";
import { AwardDecisionHistory } from "./AwardDecisionHistory";
import { ListLoadError } from "../../../common/ListLoadError";
import { useAwardSelection } from "./AwardSelectionContext";
import {
  failsMinimumRequirement,
  hasUnassessedMinimumRequirements,
} from "../minimum-requirements/MinimumRequirementOutcomeBadge";
import { StandstillChoiceField } from "./StandstillChoiceField";
import { VoluntaryStandstillField } from "./VoluntaryStandstillField";
import { SupplierCountIndicator } from "./SupplierCountIndicator";
import { REVEAL_ANCHORS } from "../revealAnchor";
import { eventIsDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";

export const SelectWinner = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const { rounds } = useRoundSelection();
  // ADR-033 §5: awarding is blocked while any negotiation round is still open.
  const hasOpenRound = rounds.some((round) => round.status === NegotiationRoundStatus.Published);
  const completableRound = [...rounds]
    .filter((round) => round.status === NegotiationRoundStatus.Published && Date.parse(round.deadline) <= Date.now())
    .sort((a, b) => a.roundNumber - b.roundNumber)
    .at(-1);
  const {
    evaluation,
    isLoading,
    reloadEvaluation,
    isReloadingEvaluation,
    winnerIds,
    toggleWinner,
    internalNote,
    setInternalNote,
    currency,
    setCurrency,
    amount,
    setAmount,
    buyerSetsStandstill,
    choosesVoluntaryStandstill,
    juryRankingDeparture,
  } = useAwardSelection();
  const selectWinnerPublicCtx = event.eventContext.type === "Public" ? event.eventContext : null;
  const maxParticipants = selectWinnerPublicCtx?.frameworkMaxParticipants ?? undefined;
  const designContest = eventIsDesignContest(event);

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (!evaluation) {
    return (
      <ListLoadError
        message={t("Could not load the evaluation.")}
        onRetry={reloadEvaluation}
        isRetrying={isReloadingEvaluation}
      />
    );
  }
  if (evaluation.bids.length === 0) {
    return (
      <Center py="16">
        <Text color="smMuted">{t("No delivered bids to award yet.")}</Text>
      </Center>
    );
  }

  return (
    <Stack
      id={REVEAL_ANCHORS.winnerSelection}
      sx={{ scrollMarginTop: "1rem" }}
      spacing="6"
      bg="smBackground"
      rounded="xl"
      border="1px solid"
      borderColor="smBorder"
      p={{ base: "4", md: "6" }}
    >
      <AwardDecisionHistory event={event} />


      {hasOpenRound && (
        <Alert status="info" borderRadius="md" alignItems="center">
          <AlertIcon />
          <Text flex="1">{t("Complete all negotiation rounds before awarding")}</Text>
          {completableRound && <CompleteRoundButton round={completableRound} />}
        </Alert>
      )}

      <Stack spacing="2">
        <AwardBidsTable evaluation={evaluation} mode="select" selectedBidIds={winnerIds} onToggle={toggleWinner} />
        <SupplierCountIndicator selectedCount={winnerIds.length} maxParticipants={maxParticipants} />
      </Stack>

      {juryRankingDeparture === "REFUSED_BY_BINDING_JURY" && (
        <Alert status="warning" rounded="md" fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            {t("The jury's decision is binding, so the winners must be the entries the jury ranked highest.")}
          </AlertDescription>
        </Alert>
      )}

      <MinimumRequirementWarning
        notFulfilled={supplierNames(evaluation.bids, winnerIds, failsMinimumRequirement)}
        unassessed={supplierNames(evaluation.bids, winnerIds, hasUnassessedMinimumRequirements)}
      />

      {!designContest && (
      <FormControl>
        <FormLabel>{t("Estimated contract value")}</FormLabel>
        <HStack>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            bg="smBackground"
            maxW="xs"
            placeholder="0"
          />
          <Select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} bg="smBackground" maxW="32">
            {Object.values(Currency).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </HStack>
      </FormControl>
      )}

      {choosesVoluntaryStandstill && <VoluntaryStandstillField />}
      {buyerSetsStandstill && (
        <StandstillChoiceField />
      )}

      <FormControl>
        <FormLabel>{t("Internal note")}</FormLabel>
        <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} bg="smBackground" rows={2} />
        <FormHelperText>{t("Only visible to your organization.")}</FormHelperText>
      </FormControl>

      <Text fontSize="sm" color="smMuted">
        {designContest
          ? t("You'll prepare and send the letters to the participants after choosing the winners.")
          : t("You'll prepare and send award letters to each supplier after awarding.")}
      </Text>

    </Stack>
  );
};

const supplierNames = (
  bids: EvalBidDto[],
  winnerIds: string[],
  matches: (bid: EvalBidDto) => boolean,
): string[] =>
  bids.filter((bid) => winnerIds.includes(bid.bidId) && matches(bid)).map((bid) => bid.supplierName);

const MinimumRequirementWarning = ({
  notFulfilled,
  unassessed,
}: {
  notFulfilled: string[];
  unassessed: string[];
}) => {
  const { t } = useTranslation();
  if (notFulfilled.length === 0 && unassessed.length === 0) return null;
  return (
    <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start">
      <AlertIcon />
      <Stack spacing="2">
        {notFulfilled.length > 0 && (
          <Box>
            <AlertTitle>
              {t("Minimum requirements are not fulfilled: {{suppliers}}", {
                suppliers: notFulfilled.join(", "),
              })}
            </AlertTitle>
            <AlertDescription>
              {t(
                "A tender that does not meet an absolute minimum requirement must be rejected, not awarded. Review the minimum requirements before you award.",
              )}
            </AlertDescription>
          </Box>
        )}
        {unassessed.length > 0 && (
          <Box>
            <AlertTitle>
              {t("Minimum requirements are not fully assessed: {{suppliers}}", {
                suppliers: unassessed.join(", "),
              })}
            </AlertTitle>
            <AlertDescription>
              {t(
                "You do not yet know whether the tender meets the minimum requirements. Assess them before you award.",
              )}
            </AlertDescription>
          </Box>
        )}
      </Stack>
    </Alert>
  );
};
